'use client';

import { API_ROUTES, useApiQuery } from '@shared/api-utils';
import { getSiteConfig, type KycVerificationMethodIcon } from '@shared/config';
import { createDebug } from '@shared/debug';
import { DrivingLicense, Passport } from '@shared/icons';
import { Verification } from '@shared/icons/system';
import type { IconProps } from '@shared/icons/types';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';
import { RegistrationStepLogo } from '../../../../../Logos/Logos';
import { SelectionCard } from '../../../../../SelectionCard/SelectionCard';
import { useMultistepForm } from '../../../../../form-controls/MultistepForm/hooks/useMultistepForm';
import { ErrorStep } from '../../components/ErrorStep';
import { useKycDialog } from '../../context/KycDialogContext';
import type { KycVerificationMethod, StartVerificationFlowResponse, VerificationMethod, VerificationMethodsStepProps } from '../../types';
import { VerificationMethodSkeleton } from './components/VerificationMethodsSkeleton';

const debug = createDebug({ isEnabled: true });

const VERIFICATION_METHOD_ICONS: Record<KycVerificationMethodIcon, React.ComponentType<IconProps>> = {
  DrivingLicense,
  Passport,
  Verification,
};

const NAME_CHANGE_MANUAL_UPLOAD_DATA_TRACK_NAME = 'Upload Document';

function VerificationMethodsStep({ onSelectMethod, onDoItLater }: VerificationMethodsStepProps) {
  const t = useTranslations('global_dialogs.kyc_verification');
  const { skipToStep } = useMultistepForm();
  const { showCloseButton, verificationMethods: verificationMethodOptions, isNameChangeFlow } = useKycDialog();
  const { PROFILE_INFO, KYC_VERIFICATION } = getSiteConfig();
  const { isUseNameChangeManualUploadCopy } = PROFILE_INFO;
  const isNameChangeBranding = isNameChangeFlow && isUseNameChangeManualUploadCopy;

  const { data: verificationMethods, isLoading, isError, refetch } = useApiQuery<KycVerificationMethod[]>(API_ROUTES.PLAYER_KYC.VERIFICATION.GET_METHODS);
  const [showError, setShowError] = useState(false);

  // Show close button when this step is active
  useEffect(() => {
    showCloseButton();
  }, [showCloseButton]);

  // Delay showing error by 1 second to allow verification methods step to be visible first
  useEffect(() => {
    if (!isError) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShowError(false);
      return;
    }
    const timeoutId = setTimeout(() => setShowError(true), 1000);
    return () => clearTimeout(timeoutId);
  }, [isError]);

  const handleRetry = () => {
    setShowError(false);
    refetch();
  };

  const handleMethodClick = async (flowId: string, methodName: string) => {
    const response: StartVerificationFlowResponse | null = await onSelectMethod(flowId);
    if (response) {
      const mapping = KYC_VERIFICATION.methods.find(siteMethod => siteMethod.name === methodName);
      if (mapping) {
        skipToStep(mapping.stepId);
      }
    }
  };

  const mappedMethods = useMemo(() => {
    debug.log('[VerificationMethodsStep] verificationMethods:', verificationMethods);
    debug.log('[VerificationMethodsStep] isLoading:', isLoading);
    debug.log('[VerificationMethodsStep] isError:', isError);

    if (!verificationMethods) {
      debug.log('[VerificationMethodsStep] No verification methods, returning empty array');
      return [];
    }

    const methods = verificationMethods
      .map(method => {
        debug.log('[VerificationMethodsStep] Processing method:', method);
        const mapping = KYC_VERIFICATION.methods.find(siteMethod => siteMethod.name === method.name);
        if (!mapping) {
          debug.log(`[VerificationMethodsStep] No mapping found for method name: ${method.name}`);
          return null;
        }

        const option = verificationMethodOptions?.find(verificationMethodOption => verificationMethodOption.name === method.name);
        if (verificationMethodOptions && !option) {
          return null;
        }

        const baseMethod: Omit<VerificationMethod, 'id'> = {
          displayName: t(`methods.${mapping.translationKey}.display_name`),
          description: t(`methods.${mapping.translationKey}.description`),
          icon: VERIFICATION_METHOD_ICONS[mapping.icon],
          name: mapping.name,
          stepId: mapping.stepId,
          dataTrackName: mapping.dataTrackName,
        };

        let optionOverrides: Partial<VerificationMethod> = {};
        if (isNameChangeFlow && method.name === 'manual-upload') {
          optionOverrides = {
            displayName: t('name_change.methods.manual_upload.display_name'),
            icon: isNameChangeBranding ? DrivingLicense : baseMethod.icon,
            dataTrackName: NAME_CHANGE_MANUAL_UPLOAD_DATA_TRACK_NAME,
            ...(isNameChangeBranding ? { description: undefined } : { description: t('name_change.methods.manual_upload.description') }),
          };
        }

        return {
          ...baseMethod,
          ...optionOverrides,
          id: method.id,
        };
      })
      .filter((method): method is VerificationMethod => method !== null);

    debug.log('[VerificationMethodsStep] Mapped methods:', methods);
    return methods;
  }, [verificationMethods, verificationMethodOptions, isLoading, isError, isNameChangeFlow, isNameChangeBranding, t, KYC_VERIFICATION.methods]);

  if (isError && showError) {
    return <ErrorStep onRetry={handleRetry} />;
  }

  return (
    <div className="flex flex-col flex-1 pt-10 md:pt-6">
      {/* Icon  */}
      {isNameChangeBranding ? (
        <Verification size="extra-extra-large" className="mb-4" data-testid="name-change-verification-icon" />
      ) : (
        <RegistrationStepLogo className="rounded-sm mb-4" />
      )}

      {/* Heading */}
      <h4 className="text-foreground mb-2">{t(isNameChangeFlow ? 'name_change.heading' : 'heading')}</h4>

      {/* Description */}
      <p className="paragraph text-foreground-subtle mb-6">{t(isNameChangeFlow ? 'name_change.description' : 'description')}</p>

      {/* Verification Methods */}
      <div className="flex flex-col gap-5 my-6" data-track-container="Methods">
        {isLoading || (isError && !showError) ? (
          <>
            <VerificationMethodSkeleton />
            <VerificationMethodSkeleton />
          </>
        ) : (
          mappedMethods.map(method => (
            <SelectionCard
              key={method.id}
              icon={method.icon}
              label={method.displayName}
              description={method.description}
              onClick={() => handleMethodClick(method.id, method.name)}
              hierarchy="default"
              data-testid={`verification-method-${method.stepId}`}
              data-track-name={method.dataTrackName}
            />
          ))
        )}
      </div>

      {/* Do it later link */}
      <div className="mt-auto pt-4" data-track-container="Actions">
        <button onClick={onDoItLater} className="text-center link-label underline w-full" data-testid="do-it-later-button" data-track-name="Cancel">
          {t('do_it_later')}
        </button>
      </div>
    </div>
  );
}

export { VerificationMethodsStep };
